import { Component, OnInit } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { ToastComponent } from './components/toast/toast.component';
import { TranslationSyncService } from './services/translation-sync.service';
import { IdleTimeoutService } from './services/idle-timeout.service';
import { DevtoolsGuardService } from './services/devtools-guard.service';
import { RouteSecurityService } from './services/route-security.service';
import { SessionFingerprintService } from './services/session-fingerprint.service';
import { TamperDetectionService } from './services/tamper-detection.service';
import { SensitiveDataGuardService } from './services/sensitive-data-guard.service';
import { VisitorTrackingService } from './services/visitor-tracking.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, ToastComponent],
  template: `
    <div id="google_translate_element" class="translate-host"></div>
    <router-outlet></router-outlet>
    <app-toast></app-toast>

    <!-- WhatsApp Floating Button -->
    <a
      href="https://wa.me/919966412732?text=Welcome%20to%20the%20Tour%20Guide%20Welfare%20Association%20of%20Ayodhya%20Ji."
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp"
      style="
        position: fixed;
        bottom: 24px;
        right: 24px;
        z-index: 9999;
        background-color: #25D366;
        border-radius: 50%;
        width: 56px;
        height: 56px;
        display: flex;
        align-items: center;
        justify-content: center;
        box-shadow: 0 4px 12px rgba(0,0,0,0.25);
        text-decoration: none;
        transition: transform 0.2s ease;
      "
      onmouseover="this.style.transform='scale(1.1)'"
      onmouseout="this.style.transform='scale(1)'"
    >
      <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 32 32" fill="white">
        <path d="M16 0C7.163 0 0 7.163 0 16c0 2.822.736 5.469 2.027 7.77L0 32l8.43-2.007A15.93 15.93 0 0 0 16 32c8.837 0 16-7.163 16-16S24.837 0 16 0zm0 29.333a13.27 13.27 0 0 1-6.77-1.853l-.485-.288-5.003 1.192 1.215-4.87-.317-.5A13.267 13.267 0 0 1 2.667 16C2.667 8.636 8.636 2.667 16 2.667S29.333 8.636 29.333 16 23.364 29.333 16 29.333zm7.27-9.87c-.398-.199-2.354-1.162-2.719-1.294-.365-.133-.63-.199-.896.199-.265.398-1.029 1.294-1.261 1.56-.232.265-.465.298-.863.1-.398-.199-1.681-.62-3.202-1.977-1.183-1.056-1.982-2.36-2.214-2.758-.232-.398-.025-.613.174-.811.179-.178.398-.465.597-.697.199-.232.265-.398.398-.664.133-.265.066-.497-.033-.697-.1-.199-.896-2.16-1.228-2.957-.323-.776-.651-.671-.896-.683l-.763-.013c-.265 0-.697.1-1.062.497-.365.398-1.394 1.362-1.394 3.322s1.427 3.854 1.626 4.12c.199.265 2.808 4.287 6.803 6.014.951.41 1.693.655 2.271.839.954.304 1.823.261 2.51.158.765-.114 2.354-.962 2.686-1.891.332-.929.332-1.726.232-1.891-.099-.166-.365-.265-.763-.464z"/>
      </svg>
    </a>
  `,
})
export class AppComponent implements OnInit {
  constructor(
    private translationSync: TranslationSyncService,
    private idleTimeout: IdleTimeoutService,
    private devtoolsGuard: DevtoolsGuardService,
    private routeSecurity: RouteSecurityService,
    private sessionFingerprint: SessionFingerprintService,
    private tamperDetection: TamperDetectionService,
    private sensitiveDataGuard: SensitiveDataGuardService,
    private visitorTracking: VisitorTrackingService
  ) {}

  ngOnInit(): void {
    // Initialize visitor tracking (must be first for accurate counting)
    this.visitorTracking.initializeVisitorTracking();

    // Activate production-only security hardening
    this.devtoolsGuard.activate();
    this.tamperDetection.activate();

    // Detect tab/window duplication attacks
    this.detectMultiTabAbuse();
  }

  /**
   * Detects if someone duplicates the tab to bypass session controls.
   * Uses BroadcastChannel to coordinate between tabs.
   */
  private detectMultiTabAbuse(): void {
    try {
      const channel = new BroadcastChannel('tgaa_session_sync');
      const tabId = crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36);

      // Announce this tab
      channel.postMessage({ type: 'TAB_OPEN', tabId });

      channel.onmessage = (event) => {
        if (event.data?.type === 'TAB_OPEN' && event.data?.tabId !== tabId) {
          // Another tab opened — not blocking, just logging for audit
          const events = JSON.parse(sessionStorage.getItem('security_events') || '[]');
          events.push({
            type: 'MULTI_TAB_DETECTED',
            timestamp: new Date().toISOString(),
            tabId: event.data.tabId
          });
          if (events.length > 20) events.shift();
          sessionStorage.setItem('security_events', JSON.stringify(events));
        }
      };
    } catch {
      // BroadcastChannel not supported — skip
    }
  }
}
